import { IntervalTimer } from './../../shared/helpers/intervalTimer';
import { TIME_SETTING_TYPE } from './../../shared/helpers/app.constants';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CompaniesService } from '../../shared/services';
import { Subscription } from 'rxjs';
import { Helper } from '../../shared/helpers/helper';
import { UserAuthService } from '../../shared/services/user-auth.service';
import { cloneDeep, isEmpty, orderBy } from 'lodash';
import { map } from 'rxjs/operators';
import { ITimeRangeFilter, BaseChartService } from '../../shared/services/charts/base-chart.service';
import { ChannelSelectionService } from '../../shared/services/channel-selection.service';
import { ChartsService } from '../../shared/services/charts.service';
import { TimeSettingsComponent } from '../../shared/components/modals/time-settings/time-settings.component';
import { NbDialogService } from '@nebular/theme';
import { IChannelSettingModel, isChannelSettingModel } from '../../shared/models/channel.model';
import { ChannelSelectionComponent } from '../../shared/components/modals/channel-selection/channel-selection.component';
import { ChannelSettingsStorageService } from '../../shared/services/channel-settings-storage.service';
import { MultiFlowDataChartComponent } from '../../shared/components/charts/multi-flow-data-chart/multi-flow-data-chart.component';
import { NavbarScaleChartService } from '../../shared/services/navbar-scale-chart.service';
import { CHART_MESSAGES, INTERVAL_CHART_REALTIME } from '../../shared/helpers/app.constants';
import { ITimeSetting } from '../../shared/models';

@Component({
  selector: 'app-equip-monitoring',
  templateUrl: './equip-monitoring.component.html',
  styleUrls: ['./equip-monitoring.component.scss']
})
export class EquipMonitoringComponent implements OnInit, OnDestroy {

  @ViewChild(MultiFlowDataChartComponent) highChart: MultiFlowDataChartComponent;

  // Button dropdown
  optionsOpened: boolean = false;
  exportList: string[] = ['Save PNG', 'Save JPEG', 'Print Chart'];

  subscriptions: Subscription[] = [];
  loading: boolean = false;
  hideButton: boolean = false;

  isDisplayTooltip: boolean = true;

  isLoadingChartData: boolean = false;
  listChannels: any[] = [];

  // Company
  companyId: number;
  companyName: string;
  companyList: any[] = [];
  selectedCompanyList: any[] = [];

  // Equipment
  equipmentId: number;
  equipmentName: string;
  equipmentList: any[] = [];
  selectedEquipmentList: any[] = [];

  // Chart
  chartInputState: any = {};
  chartInputData: any = [];
  queryParams: any = {
    wellId: '',
    treatmentId: ''
  };
  channelSettings: IChannelSettingModel;
  filterSettings: ITimeSetting = {
    type: TIME_SETTING_TYPE.OffsetTime,
    startIndex: 0,
    endIndex: 0
  };
  timeRangeFilterValues: ITimeRangeFilter;

  intervalTimer: IntervalTimer;

  constructor(
    private dialogService: NbDialogService,
    private companiesService: CompaniesService,
    private userAuthService: UserAuthService,
    private baseChartService: BaseChartService,
    private channelSelectionService: ChannelSelectionService,
    private chartsService: ChartsService,
    private channelSettingsStorageService: ChannelSettingsStorageService,
    private navbarScaleChartService: NavbarScaleChartService
  ) {
    const user = this.userAuthService.getUserInfo();
    if (user && user.companyType === 'Carbo') {
      this.getCompanyList();
    } else {
      this.hideButton = true;
      this.getListEquipments(user.companyId);
      this.companyId = user.companyId;
      this.companyName = user.company;
    }
  }

  ngOnInit() {
    this.subscriptions.push(this.navbarScaleChartService.isLoading.subscribe(value => {
      setTimeout(() => {
        this.highChart.reflow();
      });
    }));
  }

  ngOnDestroy() {
    this.stopIntervalTimer();
    Helper.unsubscribe(this.subscriptions);
  }

  getCompanyList() {
    this.subscriptions.push(
      this.companiesService.getListCompany()
        .subscribe(
          (res) => {
            if (res && res.result.items.length > 0) {
              this.companyList = res.result.items.filter(item => item.typeName !== 'Operator');
              this.companyList = orderBy(this.companyList, item => item.name);
              this.selectedCompanyList = [this.companyList[0]];
              this.companyId = this.companyList[0].id;
              this.companyName = this.companyList[0].name;
              this.getListEquipments(this.companyId);
            }
          }
        )
    );
  }

  companySelect(e: any) {
    if (this.companyId !== e.id) {
      this.companyId = e.id;
      this.companyName = e.text;
      this.getListEquipments(this.companyId);
    }
  }

  setChannelSettings() {
    // get settings from local storage
    const settings = this.channelSettingsStorageService.getByEquipment(this.equipmentId);
    if (isChannelSettingModel(settings)) {
      this.channelSettings = settings;
    } else {
      // get default if empty in local storage
      this.channelSettings = this.channelSelectionService.getDefaultSettings(this.listChannels);
    }
  }
  stopIntervalTimer() {
    if (this.intervalTimer) { this.intervalTimer.stop(); }
  }

  setIntervalTimer() {
    this.intervalTimer = new IntervalTimer(() => {
      // reload list channel if it's empty
      if (isEmpty(this.listChannels) && this.companyId && this.equipmentId) {
        this.getListChannels(this.companyId, this.equipmentId).subscribe(channels => {
          this.listChannels = channels;
          this.setChannelSettings();
        }, err => err);
      }
      // set null for auto scale x axis
      this.highChart.setAxisMinMax(null, null, false, true);
      this.intervalGetChartData(this.companyId, this.equipmentId, this.channelSettings);
    }, INTERVAL_CHART_REALTIME);
  }

  equipmentSelect(e: any) {
    if (this.equipmentId !== e.id) {
      this.equipmentId = e.id;
      this.equipmentName = e.text;
      this.isLoadingChartData = true;
      this.getListChannels(this.companyId, this.equipmentId).subscribe(channels => {
        this.listChannels = channels;
        if (this.equipmentId) {
          this.setChannelSettings();
          // clear interval previously
          this.stopIntervalTimer();
          // reset chart previously
          this.chartInputData = [];
          // realtime handler
          this.reloadChartData(this.channelSettings).then(() => {
            this.setIntervalTimer();
          });
        }
      }, err => {
        this.isLoadingChartData = false;
      });
    }
  }

  getListEquipments(companyId) {
    if (companyId) {
      this.companiesService.getCompanyId(companyId).subscribe(res => {
        if (res && res.result) {
          this.equipmentList = orderBy(res.result.equipmentDtos, item => item.name);
          if (!isEmpty(this.equipmentList)) {
            this.selectedEquipmentList = [this.equipmentList[0]];
            this.equipmentId = this.equipmentList[0].id;
            this.equipmentName = this.equipmentList[0].name;
            this.getListChannels(this.companyId, this.equipmentId).subscribe(channels => {
              this.listChannels = channels;
              if (this.equipmentId) {
                this.setChannelSettings();

                this.reloadChartData(this.channelSettings).then(() => {
                  // realtime handler
                  this.stopIntervalTimer();
                  this.setIntervalTimer();
                });

              }
            }, err => err);
          } else {
            this.selectedEquipmentList = [];
            this.listChannels = [];
            this.equipmentId = null;
            this.equipmentName = null;
            this.highChart.resetChart(true);
            setTimeout(() => {
              this.highChart.setChartMessage(CHART_MESSAGES.en.noData);
            }, 100);
          }
        }
      }, err => err);
    }
  }

  getListChannels(companyId, equipmentId) {
    return this.companiesService.getEquipmentChannels(companyId, equipmentId).pipe(map(response => {
      if (response && response.result) {
        return response.result;
      }
      return response;
    }));
  }

  getChartData(
    companyId,
    equipmentId,
    channelsMap: string[],
  ): Promise<any> {
    if (equipmentId) {
      const requestBody: any = {
        companyId,
        equipmentId,
        items: channelsMap,
      };
      return this.chartsService.getEquipmentChartData(requestBody).toPromise().then(res => {
        if (res && res.result && res.result) {
          return Promise.resolve(res.result);
        }
        return Promise.resolve(res);
      });
    } else {
      return Promise.resolve();
    }
  }

  reloadChartData(channelSettings, isNoFetchNewData?: boolean) {
    return new Promise((resolve, reject) => {
      if (channelSettings) {
        // redraw chart without call api to fetch data
        if (isNoFetchNewData) {
          this.setChartInputState(channelSettings, this.filterSettings, this.chartInputData);
          resolve(this.chartInputData);
        } else {
          const channelsMap = this.channelSelectionService.toRequestChannelMap(channelSettings);
          this.isLoadingChartData = true;
          this.getChartData(
            this.companyId,
            this.equipmentId,
            channelsMap,
          ).then(response => {
            this.isLoadingChartData = false;
            const responseInputData: any = response;
            if (responseInputData && responseInputData.equipmentData) {
              const timeRangeEquipmentData = this.baseChartService.getTimeRangeFilter(responseInputData.equipmentData[0]);
              this.timeRangeFilterValues = timeRangeEquipmentData;
              this.filterSettings = this.baseChartService.toFilterSettings(this.filterSettings.type, this.timeRangeFilterValues);
              this.chartInputData = responseInputData;
              this.setChartInputState(channelSettings, this.filterSettings, this.chartInputData);
              if (isEmpty(this.listChannels)) {
                setTimeout(() => {
                  this.highChart.setChartMessage(CHART_MESSAGES.en.noData);
                }, 100);
              }
            }
            resolve(this.chartInputData);
          }).catch(err => {
            this.isLoadingChartData = false;
            reject(err);
          });
        }
      } else {
        reject(new Error('Invalid channel setting'));
      }
    });
  }

  intervalGetChartData(companyId, equipmentId, channelSettings) {
    if (companyId && equipmentId && channelSettings) {
      const channelsMap = this.channelSelectionService.toRequestChannelMap(channelSettings);
      if (!isEmpty(channelsMap)) {
        this.getChartData(
          companyId,
          equipmentId,
          channelsMap,
        ).then(response => {
          try {
            const responseInputData = response;
            const isValidResponseData = responseInputData && responseInputData.equipmentData;
            const isValidCurrentInputData = this.chartInputData && this.chartInputData.equipmentData;
            if (isValidResponseData && isValidCurrentInputData) {
              const curEquipmentData = this.chartInputData.equipmentData[0];
              const newRealtimeData = responseInputData.equipmentData[0];
              let isHasNewRealtimeData = false;
              // case current realtime data is empty but there are new realtime data
              if (!curEquipmentData && newRealtimeData && !isEmpty(newRealtimeData.values)) {
                isHasNewRealtimeData = true;
              } else {
                const newInputRealtimeDataSliced = this.baseChartService.sliceToGetNewInputData(curEquipmentData, newRealtimeData, 'equipmentData');
                isHasNewRealtimeData = this.baseChartService.checkHasNewData(curEquipmentData, newInputRealtimeDataSliced, 'equipmentData');
              }

              // update chart series data if has new data
              if (isHasNewRealtimeData) {
                // set null for auto scale x axis
                this.highChart.setAxisMinMax(null, null, false, true);
                const timeRangeRealtimeData = this.baseChartService.getTimeRangeFilter(newRealtimeData);
                this.timeRangeFilterValues = timeRangeRealtimeData;
                this.filterSettings = this.baseChartService.toFilterSettings(this.filterSettings.type, this.timeRangeFilterValues);
                this.chartInputData = responseInputData;

                this.highChart.updateSeriesData(this.chartInputData, this.filterSettings);

                // reload list channel by wellId, treatmentId, flowPathType if no data
                // update list realtime channels
                if (isHasNewRealtimeData && isEmpty(this.listChannels)) {
                  const subRes = this.getListChannels(this.companyId, this.equipmentId).subscribe(channels => {
                    this.listChannels = channels;
                  }, err => err);

                  this.subscriptions.push(subRes);
                }
              }
            } else if (isValidResponseData && !isValidCurrentInputData) {
              this.chartInputData = responseInputData;
            }
          } catch (err) {
            throw err;
          }
        }).catch(err => {
          console.log('Equipment Realtime error: ', err);
        });
      }
    }
  }

  setChartInputState(channelSettings, filterSettings, inputData) {
    this.chartInputState = {
      ...this.chartInputState,
      channelSettings: { ...channelSettings },
      filterSettings: { ...filterSettings },
      inputData: { ...inputData }
    };
  }

  showTimeSettings() {
    this.dialogService.open(TimeSettingsComponent, {
      context: {
        model: this.filterSettings,
        timeRangeFilter: this.timeRangeFilterValues,
      }
    }).onClose.subscribe(res => {
      if (res) {
        this.filterSettings = this.baseChartService.toFilterSettings(
          res.type,
          this.timeRangeFilterValues,
          res.startTime,
          res.endTime
        );
        this.setChartInputState(this.channelSettings, this.filterSettings, this.chartInputData);
      }
    });
  }

  showChannelSettings() {
    this.dialogService.open(ChannelSelectionComponent, {
      context: {
        title: 'Channel Selection',
        equipmentChart: true,
        equipmentId: this.equipmentId,
        realtimeChannels: this.listChannels,
        settings: cloneDeep(this.channelSettings)
      }
    }).onClose.subscribe(res => {
      if (res) {
        const newSettings = this.channelSelectionService.formatChannelSettingModel(res);
        const isChangeChannels = this.channelSelectionService.checkIsChangedChannels(newSettings, this.channelSettings);
        const isWithoutReload = !isChangeChannels;
        // set new settings
        this.channelSettings = newSettings;
        // save new channel settings for current well in local storage
        this.channelSettingsStorageService.saveByEquipment(this.equipmentId, this.channelSettings);

        this.reloadChartData(this.channelSettings, isWithoutReload);
      }
    });
  }

  zoomOut() {
    this.highChart.zoomOut();
  }

  exportChart(type: string) {
    this.clickOutside();
    this.highChart.exportChart(`${this.companyName}_${this.equipmentName}`.toUpperCase(), type);
  }

  toggleDisplayTooltip(event) {
    this.isDisplayTooltip = !this.isDisplayTooltip;
    this.highChart.toggleTooltip(this.isDisplayTooltip);
  }

  openButtonDropdown() {
    this.optionsOpened = !this.optionsOpened;
  }

  clickOutside() {
    this.optionsOpened = false;
  }
}
